/* eslint-disable i18next/no-literal-string */
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import type {AskHealthReport} from 'client';
import {Link} from 'components/DSYS/Link';

import styles from './AskHealth.module.css';
import {asPercent, dayLabel, errorsAndAbandoned, weekLabel} from './format';
import {LowestAnswers} from './LowestAnswers';
import {Panel} from './Panel';
import {StatTiles} from './StatTiles';
import {VersionsHistory} from './VersionsHistory';
import {WeeklyChart} from './WeeklyChart';
import {WeeksTable} from './WeeksTable';

const AMPLITUDE_DASHBOARD_URL = 'https://app.amplitude.com/analytics/reclaim/dashboard/dly0kn82';

export const AskHealthReportView = ({report}: {report: AskHealthReport}) => {
  const weeks = report.weeks.filter((week) => week.questions > 0);
  // The tiles show the last full week; until one has turns, the week so far.
  const complete = weeks.filter((week) => week.complete);
  const latest = complete.at(-1) ?? weeks.at(-1);
  const previous = latest ? weeks[weeks.indexOf(latest) - 1] : undefined;
  // Charts go by day until there are two weeks to draw a line between.
  const byDay = weeks.length < 2;
  const periods = byDay ? (report.days ?? []).filter((day) => day.questions > 0) : weeks;
  const chartProps = {byDay, label: byDay ? dayLabel : weekLabel};

  if (weeks.length === 0) {
    return (
      <Panel>
        <Text tagName="div" color="subtle">
          No Ask turns are in {report.project} yet. Braintrust logging started Oct 28, 2026; turns
          show up here once that code is deployed.
        </Text>
      </Panel>
    );
  }

  return (
    <Stack gap="24">
      {latest ? <StatTiles latest={latest} previous={previous} /> : null}

      <div className={styles.charts}>
        <WeeklyChart
          title="Answer quality"
          description="Judge score of first answers, and the share judged right and useful. Higher is better."
          weeks={periods.filter((week) => (week.scored ?? 0) > 0)}
          {...chartProps}
          series={[
            {name: 'Quality (%)', value: (week) => asPercent(week.quality)},
            {name: 'Right and useful (%)', value: (week) => asPercent(week.right)},
          ]}
        />
        <WeeklyChart
          title="Answer time"
          description="Seconds to the full answer. The slowest are mostly ones sent to the Dash agent."
          weeks={periods}
          {...chartProps}
          series={[
            {name: 'Median (s)', value: (week) => week.median_seconds},
            {name: 'Slowest 10% (s)', value: (week) => week.p90_seconds},
          ]}
        />
        <WeeklyChart
          title="How questions end"
          description="Share of all questions. Gaps are answers Ask flagged as incomplete."
          weeks={periods}
          {...chartProps}
          series={[
            {name: 'Fully answered (%)', value: (week) => asPercent(week.answered)},
            {name: 'Answer gaps (%)', value: (week) => asPercent(week.answer_gap)},
            {name: 'Errors and gave up (%)', value: (week) => asPercent(errorsAndAbandoned(week))},
          ]}
        />
      </div>

      <LowestAnswers answers={report.lowest} />

      <Stack gap="8">
        <Text tagName="div" isBold>
          By week
        </Text>
        <WeeksTable weeks={weeks} />
      </Stack>

      <VersionsHistory versions={report.versions ?? []} />

      <Text tagName="div" size="small" color="subtle">
        General Ask turns in {report.project}, Monday to Sunday UTC. Quality is the Braintrust judge
        on first answers: 1 = right and useful, 0.5 = partly, 0 = wrong or empty. A week with few
        questions moves a lot, so read small changes as noise. History before Oct 28 is on the{' '}
        <Link to={AMPLITUDE_DASHBOARD_URL}>Amplitude dashboard</Link>.{' '}
        <Link to={report.logs_url}>Open the logs in Braintrust</Link>.
      </Text>
    </Stack>
  );
};
